import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCookieConsent, reopenCookieBanner, CONSENT_CHANGED_EVENT } from './CookieConsentBanner';

/**
 * Lightweight panel exposed on the "Your Privacy" tab that lets the user revisit
 * the cookie banner and change their decision at any point. Required by GDPR
 * Article 7.3 ("withdrawing consent must be as easy as giving it").
 */
const CookiePreferencesPanel = () => {
  const [consent, setConsent] = useState(getCookieConsent());

  // Refresh display whenever the banner writes a new choice (same-tab custom
  // event) or a different tab writes localStorage (cross-tab). Belt & braces.
  useEffect(() => {
    const onChange = () => setConsent(getCookieConsent());
    window.addEventListener('storage', onChange);
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
    };
  }, []);

  const status = !consent
    ? { label: 'Not yet decided', tone: 'amber' }
    : consent.accepted
      ? { label: 'All accepted', tone: 'emerald' }
      : { label: 'Essential only', tone: 'rose' };

  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <Card className="portfolio-card" data-testid="cookie-preferences-panel">
      <CardHeader>
        <CardTitle className="text-base sm:text-xl font-serif flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-500" />
          Privacy &amp; Cookie Preferences
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Decide what optional services may collect anonymised data from your sessions.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Current choice</div>
            <span className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-full border ${tones[status.tone]}`} data-testid="cookie-current-status">
              {status.label}
            </span>
            {consent?.timestamp && (
              <div className="text-[11px] text-slate-400 mt-1">since {new Date(consent.timestamp).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reopenCookieBanner}
              data-testid="cookie-change-choice-btn"
            >
              Change my choice
            </Button>
            <Link to="/cookie-policy" className="inline-flex items-center text-xs text-emerald-600 hover:underline">
              Cookie policy <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </div>

        <div className="pt-3 border-t border-slate-200 text-xs text-slate-600 leading-relaxed space-y-1">
          <p><span className="font-medium text-slate-800">Essential cookies:</span> always on — they keep you signed in and remember your settings.</p>
          <p><span className="font-medium text-slate-800">Sentry:</span> anonymised error reports so we can fix bugs without you having to email us.</p>
          <p><span className="font-medium text-slate-800">PostHog:</span> anonymised usage analytics and session replay (mouse moves, clicks, navigation). We use this only when you explicitly accept and you can revoke at any time.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CookiePreferencesPanel;
